import React from 'react'

const WebhooksDocs: React.FC = () => {
  return (
    <div className="mx-auto mb-10 mt-4 flex max-w-6xl flex-col gap-4 px-4 xs:px-10">
      <section className="rounded-xl border border-th-border bg-th-content p-6">
        <h2 className="mb-2 font-headline text-2xl font-bold tracking-wide">What are Webhooks?</h2>
        <div className="space-y-3">
          <p className="text-th-text-secondary">
            Webhooks let your app receive real-time notifications when something happens in your Cloud Direct account. You provide an HTTPS URL; we POST a JSON
            payload whenever an event occurs.
          </p>
        </div>
      </section>

      <section className="rounded-xl border border-th-border bg-th-content p-6">
        <h2 className="mb-2 font-headline text-2xl font-bold tracking-wide">Supported events</h2>
        <div className="space-y-3">
          <ul className="max-w-prose list-disc space-y-1 pl-6 text-th-text-secondary">
            <li>TicketCreated – fired when a new ticket is created</li>
            <li>TicketUpdated – fired when an existing ticket changes</li>
          </ul>
          <p className="text-sm text-th-text-secondary">We will add more events over time.</p>
        </div>
      </section>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <section className="rounded-xl border border-th-border bg-th-content p-6">
          <h2 className="mb-2 font-headline text-2xl font-bold tracking-wide">Creating and managing webhooks</h2>
          <div className="space-y-3">
            <p className="max-w-prose text-th-text-secondary">
              Go to Integrations → Webhooks to create, edit, enable/disable, and test webhooks. Each webhook has a name, target URL, optional authentication,
              and a set of events to subscribe to. You can send a test event to verify delivery and view the last delivery status in the webhook configuration
              panel.
            </p>
            <ul className="max-w-prose list-disc space-y-1 pl-6 text-th-text-secondary">
              <li>Enable/disable a webhook at any time.</li>
              <li>Choose one or more events to subscribe to; at least one is required.</li>
              <li>Use “Send Test” to POST a sample event to your endpoint.</li>
              <li>See “Last Call Info” for timestamp, HTTP status, and brief response details.</li>
            </ul>
          </div>
        </section>

        <section className="rounded-xl border border-th-border bg-th-content p-6">
          <h2 className="mb-2 font-headline text-2xl font-bold tracking-wide">Delivery and retries</h2>
          <div className="space-y-3">
            <ul className="max-w-prose list-disc space-y-1 pl-6 text-th-text-secondary">
              <li>We POST one request per matching webhook subscription.</li>
              <li>A 2xx response acknowledges delivery. Non-2xx triggers automatic retries.</li>
              <li>We retry up to 5 times with a short delay between attempts.</li>
              <li>Request timeout is 30 seconds.</li>
            </ul>
          </div>
        </section>
      </div>

      <section className="rounded-xl border border-th-border bg-th-content p-6">
        <h2 className="mb-2 font-headline text-2xl font-bold tracking-wide">Authentication options</h2>
        <div className="space-y-3">
          <ul className="max-w-prose list-disc space-y-1 pl-6 text-th-text-secondary">
            <li>None – no additional headers.</li>
            <li>
              Basic – we send <code className="mx-1 rounded px-1 py-0.5">Authorization: Basic &lt;base64(username:password)&gt;</code>.
            </li>
            <li>
              Header – we send a custom header of your choice (e.g. <code className="mx-1 rounded px-1 py-0.5">X-Webhook-Secret</code>).
            </li>
          </ul>
        </div>
      </section>

      <section className="rounded-xl border border-th-border bg-th-content p-6">
        <h2 className="mb-2 font-headline text-2xl font-bold tracking-wide">Event payloads</h2>
        <div className="space-y-3">
          <p className="max-w-prose text-th-text-secondary">We deliver a consistent envelope. The payload shape depends on the event.</p>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="rounded-xl border border-th-border p-4">
              <div className="font-headline text-sm font-bold">Envelope</div>
              <pre className="mt-2 overflow-x-auto rounded bg-th-content-secondary p-3 text-xs text-th-text-secondary">
                {`{
  "eventType": "TicketCreated",
  "triggeredByApp": "string|null",
  "payload": { }
}`}
              </pre>
            </div>

            <div className="rounded-xl border border-th-border p-4">
              <div className="font-headline text-sm font-bold">TicketCreated payload</div>
              <pre className="mt-2 overflow-x-auto rounded bg-th-content-secondary p-3 text-xs text-th-text-secondary">
                {`{
  "id": "string",
  "number": "string",
  "title": "string",
  "description": "string",
  "account": { "id": "string" },
  "contact": { "id": "string" },
  "assignedTo": "string",
  "category": "Request|Complaint|Incident|Event|Proactive",
  "businessImpact": "Low|Medium|High",
  "usersAffected": "Single|Multiple|All",
  "priority": "Low|Medium|High|Critical",
  "updated": "2025-01-01T00:00:00Z",
  "created": "2025-01-01T00:00:00Z",
  "state": "Open|Closed|InProgress|OnHold|Resolved|Cancelled",
  "comment": {
    "comment": "string",
    "created": "2025-01-01T00:00:00Z",
    "createdBy": "string"
  },
  "customerReference": "string"
}`}
              </pre>
            </div>

            <div className="rounded-xl border border-th-border p-4 md:col-span-2">
              <div className="font-headline text-sm font-bold">TicketUpdated payload</div>
              <pre className="mt-2 overflow-x-auto rounded bg-th-content-secondary p-3 text-xs text-th-text-secondary">
                {`{
  "changes": { },
  "previous": { }
}`}
              </pre>
            </div>
          </div>
        </div>
      </section>

      <section className="rounded-xl border border-th-border bg-th-content p-6">
        <h2 className="mb-2 font-headline text-2xl font-bold tracking-wide">About triggeredByApp</h2>
        <div className="space-y-3">
          <p className="max-w-prose text-th-text-secondary">
            The envelope includes a <code className="mx-1 rounded px-1 py-0.5">triggeredByApp</code> property. When an event is caused by an action initiated by
            one of your registered integration applications (for example, an API call your app made), this field is populated with that application&apos;s ID.
            When it is <code className="mx-1 rounded px-1 py-0.5">null</code>, the change was user- or system-initiated.
          </p>
          <ul className="max-w-prose list-disc space-y-1 pl-6 text-th-text-secondary">
            <li>
              Compare <code className="mx-1 rounded px-1 py-0.5">triggeredByApp</code> to your app&apos;s ID to detect self-originated events.
            </li>
            <li>Ignore or no-op on self-originated events to avoid update loops and feedback cycles.</li>
            <li>For critical flows, combine this check with idempotency/deduplication on your side.</li>
          </ul>
        </div>
      </section>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <section className="rounded-xl border border-th-border bg-th-content p-6">
          <h2 className="mb-2 font-headline text-2xl font-bold tracking-wide">Testing your integration</h2>
          <div className="space-y-3">
            <ul className="max-w-prose list-disc space-y-1 pl-6 text-th-text-secondary">
              <li>Use “Send Test” from the Webhook drawer to deliver a sample event.</li>
              <li>Respond quickly with a 2xx status code. Log the request body for debugging.</li>
              <li>If you return non-2xx, we will retry up to 5 times.</li>
            </ul>
          </div>
        </section>

        <section className="rounded-xl border border-th-border bg-th-content p-6">
          <h2 className="mb-2 font-headline text-2xl font-bold tracking-wide">Security and best practices</h2>
          <div className="space-y-3">
            <ul className="max-w-prose list-disc space-y-1 pl-6 text-th-text-secondary">
              <li>Always use HTTPS endpoints.</li>
              <li>Prefer a dedicated secret header or Basic auth; rotate regularly.</li>
              <li>Validate and sanitize inputs before using them.</li>
              <li>Implement idempotency on your side to avoid duplicate effects on retries.</li>
            </ul>
          </div>
        </section>
      </div>
    </div>
  )
}

export default WebhooksDocs
